import type { ApiPaginatedResponse, ApiResponse, ApiResult } from '@bcube/shared';
import { AxiosError } from 'axios';
import { api } from '@/lib/api';
import type {
  CreateSpecificClientPricePayload,
  ISpecificClientPrice,
  UpdateSpecificClientPricePayload,
} from '@/types/commercial/specific-client-prices';

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError && error.response?.data?.message) {
    return error.response.data.message;
  }
  return fallback;
}

function extractErrorFields(error: unknown): Record<string, string> | undefined {
  if (error instanceof AxiosError && error.response?.data?.fields) {
    return error.response.data.fields;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const specificClientPriceApi = {
  findMany: async (params: {
    accountId?: string;
    productId?: string;
    connectivityId?: string;
    negotiationType?: string;
    active?: boolean;
    page?: number;
    limit?: number;
  }): Promise<ISpecificClientPrice[]> => {
    const queryParams: Record<string, string | number> = {};
    if (params.accountId) queryParams.accountId = params.accountId;
    if (params.productId) queryParams.productId = params.productId;
    if (params.connectivityId) queryParams.connectivityId = params.connectivityId;
    if (params.negotiationType) queryParams.negotiationType = params.negotiationType;
    if (params.active !== undefined) queryParams.active = String(params.active);
    if (params.page) queryParams.page = params.page;
    if (params.limit) queryParams.limit = params.limit;

    const { data } = await api.get<ApiPaginatedResponse<ISpecificClientPrice>>(
      '/commercial/specific-client-prices',
      { params: queryParams },
    );
    return data.data;
  },

  create: async (
    payload: CreateSpecificClientPricePayload,
  ): Promise<ApiResult<ISpecificClientPrice>> => {
    try {
      const { data } = await api.post<ApiResponse<ISpecificClientPrice>>(
        '/commercial/specific-client-prices',
        payload,
      );
      return { success: true, data: data.data };
    } catch (error: unknown) {
      return {
        success: false,
        error: {
          global: extractErrorMessage(error, 'Erro ao criar preço específico'),
          fields: extractErrorFields(error),
        },
      };
    }
  },

  update: async (
    id: string,
    payload: UpdateSpecificClientPricePayload,
  ): Promise<ApiResult<ISpecificClientPrice>> => {
    try {
      const { data } = await api.patch<ApiResponse<ISpecificClientPrice>>(
        `/commercial/specific-client-prices/${id}`,
        payload,
      );
      return { success: true, data: data.data };
    } catch (error: unknown) {
      return {
        success: false,
        error: {
          global: extractErrorMessage(error, 'Erro ao atualizar preço específico'),
          fields: extractErrorFields(error),
        },
      };
    }
  },

  remove: async (id: string, proposalId?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (proposalId) params.proposalId = proposalId;
    await api.delete(`/commercial/specific-client-prices/${id}`, { params });
  },
};
